import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Sparkles, ChevronRight, ArrowLeft } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import SearchAutocomplete from '../../components/SearchAutocomplete';
import { getCategoryIconComponent } from '../../components/LaundryIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PriceListMenuPage() {
  const { priceListId } = useParams();
  const { t, lang, isRTL } = useLanguage();
  const [priceList, setPriceList] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plRes, catRes] = await Promise.all([
          axios.get(`${API}/price-lists/${priceListId}`),
          axios.get(`${API}/categories?price_list_id=${priceListId}&active_only=true`)
        ]);
        setPriceList(plRes.data);
        setCategories(catRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [priceListId]);

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;
  const getDesc = (item) => lang === 'ar' ? item.description_ar : item.description_en;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0056A6] border-t-transparent" />
      </div>
    );
  }

  if (!priceList) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('notFound')}</h2>
        <p className="text-slate-500 mb-4">{t('priceListNotFound')}</p>
        <Link to="/">
          <Button>
            <ArrowLeft className={`h-4 w-4 me-2 ${isRTL ? 'rotate-180' : ''}`} />
            {t('backToMenu')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0056A6] via-[#0066CC] to-[#0077EE] text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 text-sm">
              <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
              {t('backToMenu')}
            </Link>
            
            <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20 backdrop-blur-sm">
              <Sparkles className="h-3 w-3 me-1.5" />
              {t('priceList')}
            </Badge>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {getName(priceList)}
            </h1>
            
            {getDesc(priceList) && (
              <p className="text-base text-white/80 mb-6">{getDesc(priceList)}</p>
            )}
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg shadow-slate-200/50 border border-slate-100">
            <SearchAutocomplete priceListId={priceListId} />
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <section className="container mx-auto px-4 md:px-6 lg:px-8 py-12" id="categories">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">{t('ourServices')}</h2>
          <p className="text-base text-slate-500 mt-2">{t('heroSubtitle')}</p>
        </div>

        {categories.length === 0 ? (
          <p className="text-center text-slate-400 py-12">{t('noCategories')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories.map((cat, idx) => {
              const IconComponent = getCategoryIconComponent(cat.icon);
              return (
                <Link
                  key={cat.id}
                  to={`/price-list/${priceListId}/category/${cat.id}`}
                  className={`category-card group slide-up stagger-${idx + 1}`}
                  data-testid={`category-card-${cat.id}`}
                >
                  <div className="rounded-2xl border border-slate-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-lg hover:border-[#0056A6]/20 transition-all duration-300">
                    <div className="p-6">
                      {cat.image ? (
                        <div className="w-16 h-16 rounded-2xl overflow-hidden mb-4 group-hover:scale-110 transition-transform duration-300">
                          <img src={cat.image} alt={getName(cat)} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0056A6]/10 to-[#0056A6]/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <IconComponent className="h-8 w-8" color="#0056A6" />
                        </div>
                      )}
                      <h3 className="font-bold text-slate-800 text-lg mb-1 group-hover:text-[#0056A6] transition-colors">
                        {getName(cat)}
                      </h3>
                      <p className="text-sm text-slate-400">
                        {cat.subcategory_count || 0} {t('subcategories')} • {cat.product_count || 0} {t('items')}
                      </p>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-50">
                        <span className="text-sm font-medium text-[#0056A6]">{t('viewAll')}</span>
                        <ChevronRight className={`h-5 w-5 text-[#0056A6] group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
